import { useState } from 'react'
import { ExternalLink, Clock, MapPin, Monitor, Shield, FileText, Users, Settings } from 'lucide-react'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { LeadTableRow } from '@/services/mockLeads'
import { RiskScoreBar } from './RiskScoreBar'
import { PatternIndicators } from './PatternIndicators'
import { LeadActionButtons } from './LeadActionButtons'

interface LeadExpandedDetailsProps {
  lead: LeadTableRow
  onApprove: (leadId: string) => void
  onReject: (leadId: string) => void
  onFlag: (leadId: string) => void
  onNote: (leadId: string) => void
}

export function LeadExpandedDetails({
  lead,
  onApprove,
  onReject,
  onFlag,
  onNote
}: LeadExpandedDetailsProps) {
  const [activeTab, setActiveTab] = useState('risk')
  
  const formatDate = (date: Date) => {
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }
  
  return (
    <div className="border-t bg-muted/30 p-4">
      <div className="mb-4 flex items-start justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold">
              {lead.firstName} {lead.lastName}
            </h3>
            <Badge variant="outline">
              ID: {lead.id}
            </Badge>
            <Badge variant="secondary" className="capitalize">
              {lead.source}
            </Badge>
          </div>
          
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {formatDate(lead.timestamp)}
            </div>
            
            <div className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              {lead.zip}
            </div>
            
            <div className="flex items-center gap-1">
              <Monitor className="h-4 w-4" />
              {lead.ipType}
            </div>
          </div>
        </div>
        
        <div className="text-right space-y-2">
          <RiskScoreBar score={lead.riskScore} size="lg" />
          
          <LeadActionButtons
            lead={lead}
            onApprove={onApprove}
            onReject={onReject}
            onFlag={onFlag}
            onNote={onNote}
          />
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="risk" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Risk Analysis
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Server Logs
          </TabsTrigger>
          <TabsTrigger value="clarity" className="flex items-center gap-2">
            <Monitor className="h-4 w-4" />
            User Session
          </TabsTrigger>
          <TabsTrigger value="related" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Related Leads
          </TabsTrigger>
          <TabsTrigger value="actions" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Actions
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="risk" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-medium">Risk Factors</h4>
              
              <div className="space-y-3">
                {lead.riskFactors.map((factor, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-card rounded-lg border">
                    <div>
                      <div className="font-medium text-sm">{factor.type}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {factor.description}
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="font-medium">{factor.weight.toFixed(1)}</div>
                      <Badge 
                        variant="secondary" 
                        className={`text-xs ${
                          factor.severity === 'critical' ? 'text-red-600' :
                          factor.severity === 'high' ? 'text-orange-600' :
                          factor.severity === 'medium' ? 'text-yellow-600' :
                          'text-green-600'
                        }`}
                      >
                        {factor.severity}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="space-y-4">
              <h4 className="font-medium">Pattern Detection</h4>
              
              <div className="p-3 bg-card rounded-lg border space-y-3">
                <PatternIndicators patterns={lead.patterns} layout="vertical" />
                
                {Object.values(lead.patterns).some(Boolean) && (
                  <div className="pt-3 border-t">
                    <div className="text-sm font-medium text-red-600">
                      Multi-pattern fraud detected
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Multiple fraud indicators increase confidence
                    </div>
                  </div>
                )}
              </div>
              
              <div className="p-3 bg-card rounded-lg border">
                <div className="text-sm font-medium mb-2">ML Model Confidence</div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-muted rounded-full h-2">
                    <div 
                      className="h-2 bg-primary rounded-full transition-all"
                      style={{ width: `${lead.mlConfidence * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium">
                    {(lead.mlConfidence * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="logs" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-3">Request Headers</h4>
              <div className="bg-card border rounded-lg p-3 font-mono text-xs space-y-1">
                <div><span className="text-muted-foreground">User-Agent:</span> {lead.userAgent}</div>
                <div><span className="text-muted-foreground">IP:</span> {lead.ip}</div>
                <div><span className="text-muted-foreground">Referrer:</span> {lead.referrer}</div>
                <div><span className="text-muted-foreground">Timestamp:</span> {lead.timestamp.toISOString()}</div>
              </div>
            </div>
            
            <div>
              <h4 className="font-medium mb-3">Form Data</h4>
              <div className="bg-card border rounded-lg p-3 font-mono text-xs space-y-1">
                <div><span className="text-muted-foreground">firstName:</span> {lead.firstName}</div>
                <div><span className="text-muted-foreground">lastName:</span> {lead.lastName}</div>
                <div><span className="text-muted-foreground">email:</span> {lead.email}</div>
                <div><span className="text-muted-foreground">phone:</span> {lead.phone}</div>
                <div><span className="text-muted-foreground">zip:</span> {lead.zip}</div>
              </div>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="clarity" className="space-y-4 mt-4">
          <div className="text-center py-8">
            <Monitor className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h4 className="font-medium mb-2">User Session Recording</h4>
            <p className="text-sm text-muted-foreground mb-4">
              View the user's interaction with the form to identify suspicious behavior patterns.
            </p>
            
            <Button variant="outline" asChild>
              <a 
                href={`https://clarity.microsoft.com/session/${lead.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open in Microsoft Clarity
              </a>
            </Button>
            
            <div className="grid grid-cols-3 gap-4 mt-6 text-sm">
              <div className="p-3 bg-muted rounded-lg">
                <div className="font-medium">Session Duration</div>
                <div className="text-muted-foreground">2m 34s</div>
              </div>
              
              <div className="p-3 bg-muted rounded-lg">
                <div className="font-medium">Page Views</div>
                <div className="text-muted-foreground">3 pages</div>
              </div>
              
              <div className="p-3 bg-muted rounded-lg">
                <div className="font-medium">Form Time</div>
                <div className="text-muted-foreground">12s</div>
              </div>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="related" className="space-y-4 mt-4">
          <div className="space-y-4">
            <h4 className="font-medium">Related Leads Analysis</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-3 bg-card rounded-lg border">
                <div className="font-medium text-sm mb-2">Same IP Address</div>
                <div className="text-sm text-muted-foreground">
                  2 other leads from {lead.ip} in the last 24 hours
                </div>
                <Button variant="outline" size="sm" className="mt-2">
                  View IP History
                </Button>
              </div>
              
              <div className="p-3 bg-card rounded-lg border">
                <div className="font-medium text-sm mb-2">Email Pattern</div>
                <div className="text-sm text-muted-foreground">
                  No other leads with similar email pattern
                </div>
              </div>
              
              <div className="p-3 bg-card rounded-lg border">
                <div className="font-medium text-sm mb-2">Phone Sequence</div>
                <div className="text-sm text-muted-foreground">
                  Part of 8-lead sequential phone attack
                </div>
                <Button variant="outline" size="sm" className="mt-2">
                  View Sequence
                </Button>
              </div>
              
              <div className="p-3 bg-card rounded-lg border">
                <div className="font-medium text-sm mb-2">Geographic Cluster</div>
                <div className="text-sm text-muted-foreground">
                  15 leads from zip {lead.zip} today
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="actions" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-medium">Quick Actions</h4>
              
              <div className="space-y-2">
                <LeadActionButtons
                  lead={lead}
                  onApprove={onApprove}
                  onReject={onReject}
                  onFlag={onFlag}
                  onNote={onNote}
                />
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <Button variant="outline" className="w-full justify-start">
                  Add to Whitelist
                </Button>
                
                <Button variant="outline" className="w-full justify-start">
                  Add to Blacklist
                </Button>
                
                <Button variant="outline" className="w-full justify-start">
                  Export Lead Data
                </Button>
              </div>
            </div>
            
            <div className="space-y-4">
              <h4 className="font-medium">Impact Analysis</h4>
              
              <div className="p-3 bg-card rounded-lg border">
                <div className="text-sm font-medium mb-2">Financial Impact</div>
                <div className={`text-lg font-bold ${
                  lead.costImpact > 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {lead.costImpact > 0 ? '+' : ''}${Math.abs(lead.costImpact)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {lead.costImpact > 0 
                    ? 'Amount saved by blocking fraudulent lead'
                    : 'Potential revenue if lead is legitimate'
                  }
                </div>
              </div>
              
              <div className="p-3 bg-card rounded-lg border">
                <div className="text-sm font-medium mb-2">Recommendation</div>
                <div className={`font-medium ${
                  lead.riskScore >= 70 ? 'text-red-600' :
                  lead.riskScore >= 40 ? 'text-yellow-600' : 'text-green-600'
                }`}>
                  {lead.riskScore >= 70 ? 'REJECT' :
                   lead.riskScore >= 40 ? 'MANUAL REVIEW' : 'APPROVE'}
                </div>
                <div className="text-xs text-muted-foreground">
                  Based on risk score and pattern analysis
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}